import { useEffect, useRef, useState } from "react";
import { BookOpen, Loader2, Trash2, Sparkles, FileUp, Link as LinkIcon, FileText, AlertCircle } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
    interpretBookNotes,
    importBookNoteFile,
    importBookNoteChunked,
    importBookNoteUrl,
    listBookNotes,
    deleteBookNote,
    fetchBillingStatus,
} from "@/lib/api";
import { useGardens } from "@/contexts/GardenContext";

// Any file over this size uses the chunked endpoint to bypass the
// Kubernetes ingress body-size limit (~25 MB on most Emergent deploys).
const CHUNKED_THRESHOLD_BYTES = 20 * 1024 * 1024;
const DEFAULT_FREE_CAP_BYTES = 20 * 1024 * 1024;

export default function BookNotes() {
    const { activeGardenId } = useGardens();
    const [maxBytes, setMaxBytes] = useState(DEFAULT_FREE_CAP_BYTES);
    // Paste flow
    const [pasteSource, setPasteSource] = useState("");
    const [pasteText, setPasteText] = useState("");
    const [pasteSubmitting, setPasteSubmitting] = useState(false);

    // File flow
    const [file, setFile] = useState(null);
    const [fileSource, setFileSource] = useState("");
    const [fileSubmitting, setFileSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);   // {sent, total, chunk, chunks}
    const fileInputRef = useRef(null);

    // URL flow
    const [url, setUrl] = useState("");
    const [urlSource, setUrlSource] = useState("");
    const [urlSubmitting, setUrlSubmitting] = useState(false);

    // History
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(true);

    const load = async () => {
        setHistoryLoading(true);
        try {
            setHistory(await listBookNotes());
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("notes history load failed:", e?.message);
            setHistory([]);
        } finally {
            setHistoryLoading(false);
        }
    };

    useEffect(() => {
        load();
        // Pull the user's current PDF cap from billing status so Plus &
        // Community users see 150 MB not the 20 MB default.
        fetchBillingStatus()
            .then((s) => {
                const mb = s?.limits?.pdf_mb || 20;
                setMaxBytes(mb * 1024 * 1024);
            })
            .catch(() => {});
    }, []);

    // ----- Submit handlers -----

    const submitPaste = async () => {
        if (pasteText.trim().length < 20) {
            toast.error("Paste at least a sentence or two of notes.");
            return;
        }
        setPasteSubmitting(true);
        try {
            await interpretBookNotes({ notes: pasteText, source: pasteSource, garden_id: activeGardenId || undefined });
            toast.success("rooteed read your notes");
            setPasteText("");
            setPasteSource("");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        } finally {
            setPasteSubmitting(false);
        }
    };

    const submitFile = async () => {
        if (!file) { toast.error("Choose a PDF or EPUB first"); return; }
        if (file.size > maxBytes) {
            toast.error(`File too large — your current plan allows up to ${Math.round(maxBytes / (1024 * 1024))} MB. Upgrade to Plus for 150 MB.`);
            return;
        }
        setFileSubmitting(true);
        setUploadProgress(null);
        const isLarge = file.size > CHUNKED_THRESHOLD_BYTES;
        const t = toast.loading(isLarge
            ? `Uploading ${file.name} in chunks — 0%`
            : `Reading ${file.name}…`);
        try {
            if (isLarge) {
                await importBookNoteChunked({
                    file,
                    source: fileSource,
                    garden_id: activeGardenId || null,
                    onProgress: (p) => {
                        setUploadProgress(p);
                        const pct = Math.round((p.sent / p.total) * 100);
                        toast.loading(`Uploading ${file.name} — ${pct}% (chunk ${p.chunk}/${p.chunks})`, { id: t });
                    },
                    onStage: (status, progress) => {
                        setUploadProgress({ sent: file.size, total: file.size, chunk: 0, chunks: 0, stage: status });
                        const label = progress || (
                            status === "stitching" ? "Assembling the file…"
                            : status === "extracting" ? "Reading the PDF…"
                            : status === "interpreting" ? "rooteed is reading the book…"
                            : "Processing…"
                        );
                        toast.loading(label, { id: t });
                    },
                });
            } else {
                await importBookNoteFile(file, fileSource, activeGardenId || undefined);
            }
            toast.success("Book read & interpreted", { id: t });
            setFile(null);
            setFileSource("");
            setUploadProgress(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
            await load();
        } catch (e) {
            const detail = e.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : (detail?.message || e.message), { id: t });
        } finally {
            setFileSubmitting(false);
        }
    };

    const submitUrl = async () => {
        if (!/^https?:\/\//i.test(url.trim())) {
            toast.error("Enter a full URL starting with http:// or https://");
            return;
        }
        setUrlSubmitting(true);
        const t = toast.loading("Fetching link…");
        try {
            await importBookNoteUrl(url.trim(), urlSource, activeGardenId || undefined);
            toast.success("Link read & interpreted", { id: t });
            setUrl("");
            setUrlSource("");
            await load();
        } catch (e) {
            const detail = e.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : (detail?.message || e.message), { id: t });
        } finally {
            setUrlSubmitting(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this note?")) return;
        await deleteBookNote(id);
        await load();
    };

    // ----- Render -----

    return (
        <div className="space-y-10" data-testid="booknotes-page">
            <PageHeader
                overline="Library"
                title="Book Notes Interpreter"
                description="Paste notes, upload a PDF / EPUB, or share a link. rooteed summarises ideas in its own words and applies them to your beds — never reproducing copyrighted text."
            />

            <Tabs defaultValue="paste" className="journal-card p-6 md:p-8">
                <TabsList className="bg-[var(--vm-input)]" data-testid="booknotes-tabs">
                    <TabsTrigger value="paste" data-testid="booknotes-tab-paste">
                        <FileText className="w-3.5 h-3.5 mr-1.5" /> Paste notes
                    </TabsTrigger>
                    <TabsTrigger value="upload" data-testid="booknotes-tab-upload">
                        <FileUp className="w-3.5 h-3.5 mr-1.5" /> Upload PDF / EPUB
                    </TabsTrigger>
                    <TabsTrigger value="url" data-testid="booknotes-tab-url">
                        <LinkIcon className="w-3.5 h-3.5 mr-1.5" /> From a link
                    </TabsTrigger>
                </TabsList>

                {/* PASTE */}
                <TabsContent value="paste" className="mt-6">
                    <div className="grid gap-4">
                        <div className="grid gap-2">
                            <Label>Source (optional)</Label>
                            <Input
                                data-testid="booknote-paste-source"
                                placeholder="e.g. The Garden Apothecary, Becky Cole"
                                value={pasteSource}
                                onChange={e => setPasteSource(e.target.value)}
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label>Your notes</Label>
                            <Textarea
                                data-testid="booknote-paste-text"
                                rows={8}
                                placeholder="Paste your handwritten notes, summaries or quotes here. Don't worry about formatting."
                                value={pasteText}
                                onChange={e => setPasteText(e.target.value)}
                                className="bg-[var(--vm-input)] border-[var(--vm-border)]"
                            />
                        </div>
                        <div>
                            <Button
                                onClick={submitPaste}
                                disabled={pasteSubmitting}
                                data-testid="booknote-paste-submit"
                                className="rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)] px-6"
                            >
                                {pasteSubmitting
                                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reading…</>
                                    : <><Sparkles className="w-4 h-4 mr-2" /> Apply to my garden</>}
                            </Button>
                        </div>
                    </div>
                </TabsContent>

                {/* UPLOAD */}
                <TabsContent value="upload" className="mt-6">
                    <div className="grid gap-4">
                        <div className="grid gap-2">
                            <Label>Source (optional)</Label>
                            <Input
                                data-testid="booknote-file-source"
                                placeholder="Book title, author"
                                value={fileSource}
                                onChange={e => setFileSource(e.target.value)}
                            />
                        </div>
                        <div
                            onClick={() => fileInputRef.current?.click()}
                            className="border-2 border-dashed border-[var(--vm-border)] rounded-lg p-10 text-center cursor-pointer hover:border-[var(--vm-sage)] transition-colors"
                            data-testid="booknote-file-dropzone"
                        >
                            <FileUp className="w-7 h-7 mx-auto text-[var(--vm-accent)] mb-3" strokeWidth={1.5} />
                            <p className="font-serif-display text-xl text-[var(--vm-text)] mb-1">
                                {file ? file.name : "Choose a PDF or EPUB"}
                            </p>
                            <p className="text-xs text-[var(--vm-text-muted)]">
                                {file
                                    ? `${(file.size / 1024 / 1024).toFixed(2)} MB · ready to read`
                                    : `Click to browse · max ${Math.round(maxBytes / (1024 * 1024))} MB · scanned PDFs without OCR won't work`}
                            </p>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".pdf,.epub,application/pdf,application/epub+zip"
                                className="hidden"
                                data-testid="booknote-file-input"
                                onChange={e => setFile(e.target.files?.[0] || null)}
                            />
                        </div>
                        {uploadProgress && (
                            <div className="space-y-1" data-testid="booknote-upload-progress">
                                <div className="h-2 bg-[var(--vm-input)] rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-[var(--vm-accent)] transition-all duration-200"
                                        style={{ width: `${Math.round((uploadProgress.sent / uploadProgress.total) * 100)}%` }}
                                    />
                                </div>
                                <p className="text-xs text-[var(--vm-text-muted)]">
                                    {(uploadProgress.sent / 1024 / 1024).toFixed(1)} / {(uploadProgress.total / 1024 / 1024).toFixed(1)} MB
                                    {" · "}chunk {uploadProgress.chunk}/{uploadProgress.chunks}
                                </p>
                            </div>
                        )}
                        <div>
                            <Button
                                onClick={submitFile}
                                disabled={fileSubmitting || !file}
                                data-testid="booknote-file-submit"
                                className="rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)] px-6"
                            >
                                {fileSubmitting
                                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reading book…</>
                                    : <><Sparkles className="w-4 h-4 mr-2" /> Apply to my garden</>}
                            </Button>
                        </div>
                        <div className="flex items-start gap-2 text-xs text-[var(--vm-text-muted)] bg-[var(--vm-input)]/50 rounded-md p-3">
                            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[var(--vm-sage)]" />
                            <span>
                                Your file is read once and discarded — rooteed never stores or shares the original.
                                Long books are summarised; the interpretation focuses on what applies to your garden.
                            </span>
                        </div>
                    </div>
                </TabsContent>

                {/* URL */}
                <TabsContent value="url" className="mt-6">
                    <div className="grid gap-4">
                        <div className="grid gap-2">
                            <Label>URL</Label>
                            <Input
                                data-testid="booknote-url-input"
                                placeholder="https://example.com/article-or-pdf"
                                value={url}
                                onChange={e => setUrl(e.target.value)}
                            />
                            <div className="text-xs text-[var(--vm-text-muted)]">
                                Works with article URLs and direct PDF links. Login-walled pages won't be reachable.
                            </div>
                        </div>
                        <div className="grid gap-2">
                            <Label>Source label (optional)</Label>
                            <Input
                                data-testid="booknote-url-source"
                                placeholder="A friendly name to remember it by"
                                value={urlSource}
                                onChange={e => setUrlSource(e.target.value)}
                            />
                        </div>
                        <div>
                            <Button
                                onClick={submitUrl}
                                disabled={urlSubmitting || !url.trim()}
                                data-testid="booknote-url-submit"
                                className="rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)] px-6"
                            >
                                {urlSubmitting
                                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Fetching & reading…</>
                                    : <><Sparkles className="w-4 h-4 mr-2" /> Fetch & apply</>}
                            </Button>
                        </div>
                    </div>
                </TabsContent>
            </Tabs>

            {/* History */}
            <section data-testid="booknotes-history">
                <div className="overline text-[var(--vm-sage)] mb-3">Saved interpretations</div>
                {historyLoading ? (
                    <div className="text-[var(--vm-text-muted)] flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                    </div>
                ) : history.length === 0 ? (
                    <div className="journal-card p-8 text-center text-[var(--vm-text-secondary)]">
                        <BookOpen className="w-8 h-8 mx-auto text-[var(--vm-sage)] mb-3" strokeWidth={1.5} />
                        No saved notes yet.
                    </div>
                ) : (
                    <div className="space-y-4">
                        {history.map(h => (
                            <div key={h.id} className="journal-card p-6" data-testid={`booknote-${h.id}`}>
                                <div className="flex items-start justify-between mb-3">
                                    <div className="min-w-0">
                                        <div className="overline text-[var(--vm-sage)] text-xs truncate">
                                            {h.source || "unspecified"}
                                        </div>
                                        <div className="text-xs text-[var(--vm-text-muted)] mt-1">
                                            {new Date(h.created_at).toLocaleDateString("en-IE", { dateStyle: "long" })}
                                        </div>
                                    </div>
                                    <Button size="icon" variant="ghost" onClick={() => remove(h.id)} data-testid={`delete-booknote-${h.id}`}>
                                        <Trash2 className="w-4 h-4 text-[var(--vm-secondary)]" />
                                    </Button>
                                </div>
                                <div className="text-sm text-[var(--vm-text)] whitespace-pre-wrap leading-relaxed">
                                    {h.interpretation}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
